import * as React from 'react';

const { useState, memo } = React;

const AUTO_COLLAPSE_DEPTH = 2;
const LARGE_ARRAY = 20;
const LARGE_OBJECT = 20;
const LAZY_LIMIT = 200;

function Primitive({ value }: { value: unknown }) {
  if (value === null)                 return <span className="j-null">null</span>;
  if (typeof value === 'string')      return <span className="j-string">{JSON.stringify(value)}</span>;
  if (typeof value === 'number')      return <span className="j-number">{String(value)}</span>;
  if (typeof value === 'boolean')     return <span className="j-bool">{String(value)}</span>;
  if (value === undefined)            return <span className="j-null">undefined</span>;
  return <span className="j-other">{String(value)}</span>;
}

function KeyLabel({ k }: { k: string | number }) {
  return typeof k === 'number'
    ? <span className="j-index">[{k}]</span>
    : <span className="j-key">{k}:</span>;
}

interface NodeProps {
  value: unknown;
  keyName?: string | number | null;
  depth: number;
}

const JsonNode = memo(function JsonNode({ value, keyName = null, depth }: NodeProps) {
  const isArr = Array.isArray(value);
  const isObj = !isArr && value !== null && typeof value === 'object';

  if (!isArr && !isObj) {
    return (
      <div className="j-node">
        {keyName !== null && <><KeyLabel k={keyName} /> </>}
        <Primitive value={value} />
      </div>
    );
  }

  const entries: [string | number, unknown][] = isArr
    ? (value as unknown[]).map((v, i) => [i, v])
    : Object.entries(value as Record<string, unknown>);

  const size = entries.length;
  const big = isArr ? size > LARGE_ARRAY : size > LARGE_OBJECT;
  const startOpen = depth < AUTO_COLLAPSE_DEPTH && !big;

  const [open, setOpen] = useState(startOpen);
  const [limit, setLimit] = useState(LAZY_LIMIT);

  const preview = isArr
    ? `Array(${size})`
    : `Object {${entries.slice(0, 3).map(([k]) => k).join(', ')}${size > 3 ? ', …' : ''}}`;

  return (
    <div className="j-node">
      <div className="j-header" onClick={() => setOpen(o => !o)}>
        <span className="j-toggle">{open ? '▾' : '▸'}</span>
        {keyName !== null && <KeyLabel k={keyName} />}
        <span className="j-preview">{preview}</span>
      </div>
      {open && (
        <div className="j-children">
          {entries.slice(0, limit).map(([k, v]) => (
            <JsonNode key={String(k)} value={v} keyName={k} depth={depth + 1} />
          ))}
          {entries.length > limit && (
            <div
              className="j-more"
              onClick={(e) => { e.stopPropagation(); setLimit(l => l + LAZY_LIMIT); }}
            >
              … show {Math.min(LAZY_LIMIT, entries.length - limit)} more ({entries.length - limit} hidden)
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default function JsonViewer({ data }: { data: unknown }) {
  return (
    <div className="json-viewer">
      {data === undefined || data === null
        ? <Primitive value={data} />
        : typeof data !== 'object'
          ? <Primitive value={data} />
          : <JsonNode value={data} keyName={null} depth={0} />}
    </div>
  );
}

